import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useLivePageContent } from '@/hooks/usePageContent';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Leaf, Check } from 'lucide-react';
import { Sila3DExplorer } from '@/components/public/Sila3DExplorer';
import { SilaHero } from '@/components/public/SilaHero';
import { BookingDialog } from '@/components/public/BookingDialog';

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

/**
 * Pagina area riutilizzabile (Experience / Dove Mangiare / Alloggi / Servizi / Parchi).
 * I contenuti di default sono nel `config`; l'admin puo' sovrascriverli dal CRM
 * tramite l'endpoint /api/cms/page/{slug} (titolo, lead, immagine hero, testi, ecc.).
 * Se `config.embedMap` e' true, incorpora la mappa 3D del Parco con prenotazione.
 */
export const AreaPage = ({ slug, config, extra }) => {
  const ov = useLivePageContent(slug);
  const [areas, setAreas] = useState([]);
  const [booking, setBooking] = useState({ open: false, area: null });

  useEffect(() => {
    if (!config.embedMap) return undefined;
    let alive = true;
    api.get('/parco/public/areas').then(({ data }) => { if (alive) setAreas(data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [config.embedMap]);

  const eyebrow = str(ov.eyebrow) || config.eyebrow;
  const title = str(ov.title) || config.title;
  const lead = str(ov.lead) || config.lead;
  const heroImage = str(ov.heroImage) || config.heroImage;
  const intro = str(ov.intro) || config.intro;
  const features = Array.isArray(ov.features) && ov.features.length ? ov.features : config.features;
  const bullets = Array.isArray(ov.bullets) && ov.bullets.length ? ov.bullets : (config.bullets || []);

  return (
    <PublicLayout>
      <Seo title={title} description={lead} path={`/${slug === 'experience' ? 'esperienze' : slug}`} image={heroImage} />

      {/* HERO animato in stile "La Sila" (Ken Burns + testo animato + onda) */}
      <SilaHero image={heroImage} badge={eyebrow} title={title} subtitle={lead} minH="62vh" testId={`area-hero-${slug}`}>
        {config.embedMap ? (
          <Button asChild size="lg" className="rounded-full" data-testid={`area-map-${slug}`}>
            <a href="#mappa-3d">Esplora la mappa 3D<ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        ) : config.mapTo ? (
          <Button asChild size="lg" className="rounded-full" data-testid={`area-map-${slug}`}>
            <Link to={config.mapTo}>Apri la mappa 3D<ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        ) : null}
      </SilaHero>

      {/* MAPPA 3D INCORPORATA (es. Parchi) */}
      {config.embedMap && (
        <section id="mappa-3d" className="sila-section scroll-mt-20 py-12 sm:py-16" data-testid={`area-map-embed-${slug}`}>
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]"><Leaf className="h-3.5 w-3.5" />Sila 3D Explorer</span>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Esplora il Parco in 3D</h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">Ruota la mappa, passa il mouse sugli itinerari luminosi, apri i punti panoramici e prenota il tuo tour. Usa l'interruttore Satellite / Ibrido in alto.</p>
            </div>
            {config.mapTo && (
              <Button asChild variant="outline" className="rounded-full">
                <Link to={config.mapTo}>Apri a schermo intero<ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            )}
          </div>
          <Sila3DExplorer areas={areas} onBook={(area) => setBooking({ open: true, area })} />
          {areas.length === 0 && (
            <p className="mt-4 rounded-xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="area-map-empty">Nessun itinerario pubblicato al momento. Torna presto: le guide stanno tracciando i primi percorsi.</p>
          )}
        </section>
      )}

      {/* INTRO */}
      {intro && (
        <section className="sila-section py-14 sm:py-20">
          <div className="max-w-3xl">
            <p className="text-lg leading-relaxed text-muted-foreground sm:text-xl">{intro}</p>
          </div>
          {bullets.length > 0 && (
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-sm leading-relaxed text-foreground">{str(b)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* FEATURES / GALLERY con zoom moderno */}
      {features && features.length > 0 && (
        <section className="sila-section pb-20">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <article key={i} className="group overflow-hidden rounded-[24px] border border-border bg-card shadow-[var(--shadow-sm)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]" data-testid={`area-feature-${slug}-${i}`}>
                <div className="relative h-52 overflow-hidden">
                  <img loading="lazy" src={str(f.img)} alt={str(f.title)} className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-110" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  {f.tag && <Badge className="absolute left-4 top-4 rounded-full bg-background/85 text-foreground backdrop-blur">{str(f.tag)}</Badge>}
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold">{str(f.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{str(f.desc)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* SEZIONE EXTRA (es. anteprima esperienze reali dal catalogo) */}
      {extra}

      {/* CTA band */}
      <section className="sila-section pb-24">
        <div className="grain-overlay flex flex-col items-start gap-5 overflow-hidden rounded-[28px] bg-primary p-8 text-primary-foreground sm:flex-row sm:items-center sm:justify-between sm:p-12">
          <div>
            <h2 className="font-display text-2xl font-bold sm:text-3xl">{config.ctaTitle || 'Vuoi vivere la Sila?'}</h2>
            <p className="mt-2 max-w-xl text-primary-foreground/85">{config.ctaText || 'Scrivici o registrati: costruiamo insieme la tua esperienza.'}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" variant="secondary" className="rounded-full" data-testid={`area-cta-${slug}`}>
              <Link to={config.ctaTo || '/contatti'}>{config.ctaLabel || 'Contattaci'}<ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/la-sila">Scopri La Sila</Link>
            </Button>
          </div>
        </div>
      </section>
      <BookingDialog open={booking.open} onOpenChange={(o) => setBooking((b) => ({ ...b, open: o }))} area={booking.area} />
    </PublicLayout>
  );
};

export default AreaPage;
